import { FC, useRef, useState } from "react";

import Stepper from "../molecules/Stepper/Stepper";
import BellyChoice from "../BellyChoice";
import MeasuredBy from "../molecules/MeasuredBy";

import { useTranslation } from "react-i18next";

import {
  createCloudAssetResolver,
  GENDERS,
  MODAL_STEPS,
} from "../../constants/modal";

import {
  titleStyles,
  disabledStylesContinue,
  activeStylesContinue,
} from "../../configs";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";

import { findBrandByDomain } from "../../configs/configLoader";
import { useUserContext } from "../../store/userContext";
import {
  hoverStylesContinue,
  welcomeScreenUI,
} from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import ScanCTA from "../DrawerModal/components/ScanCTA";
import { SLEEVES_CLOUD_PATHS } from "../../constants/imagesUrls";

import "./index.css";

interface IPropsStepSleeves {
  step: {
    number: number;
  } | null;
  gender: string;
  nextStep: () => void;
  setStep: (
    value: React.SetStateAction<{
      number: number;
    } | null>,
  ) => void;
  setPreviousStep: React.Dispatch<
    React.SetStateAction<{
      number: number;
    } | null>
  >;
  value: {
    current?: number;
  };
  descriptionStyles: React.CSSProperties;
}

const StepSleeves: FC<IPropsStepSleeves> = ({
  step,
  gender,
  nextStep,
  setStep,
  setPreviousStep,
  value,
  descriptionStyles,
}) => {
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState<number | undefined>(value?.current);

  const userContext = useUserContext() || null;

  const { t } = useTranslation("components/sleeves");

  const brandDefined = findBrandByDomain();

  const continueButtonRef = useRef<HTMLButtonElement | null>(null);

  const [hovered, setHovered] = useState(false);

  const isIntersport = brandDefined?.name === "Intersport";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isSRP = brandDefined?.name === "SRP";
  const isHast = brandDefined?.name === "Hast";
  const isTheodore = brandDefined?.name === "Theodore";
  const isPeserico = brandDefined?.name === "Peserico";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isMobileVB = isMobile && isVB;
  const isGivenchyDesktop = isGivenchy && !isMobile;
  const isGivenchyMobile = isGivenchy && isMobile;

  const continueButtonStyles = !selected
    ? disabledStylesContinue
    : activeStylesContinue;

  const handleImagesGap = () => {
    if (isGivenchy) return "20px";
    if (isTDF) return "16px";
    if (isMobileVB) return "0px";
    return "8px";
  };

  const CTAStyles: React.CSSProperties = {
    ...continueButtonStyles,
    ...(hovered && selected ? hoverStylesContinue : {}),
    marginTop: isGivenchy && isMobile ? "60px" : "20px",
    textTransform:
      continueButtonStyles.textTransform === "capitalize"
        ? "none"
        : continueButtonStyles.textTransform,
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    position: isGivenchyDesktop ? "fixed" : "relative",
    bottom: isGivenchyDesktop ? "150px" : 0,
    width: isGivenchyDesktop ? "calc(100% - 160px)" : "",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && selected ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && selected ? 1 : 0,
    transform: hovered && selected ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const getSleeveAsset = createCloudAssetResolver(SLEEVES_CLOUD_PATHS);

  return (
    <>
      <div
        className="belly-description"
        style={{
          ...descriptionStyles,
          textAlign: titleStyles.textAlign as React.CSSProperties["textAlign"],
          justifyContent:
            titleStyles.textAlign as React.CSSProperties["justifyContent"],
          textTransform:
            descriptionStyles.textTransform === "capitalize"
              ? "none"
              : (descriptionStyles.textTransform as React.CSSProperties["textTransform"]),
          fontStyle: (welcomeScreenUI.input_fields.title as any)
            .fontStyle as React.CSSProperties["fontStyle"],
          marginTop: isGivenchy
            ? "10px"
            : isTDF
              ? "0px"
              : descriptionStyles.marginTop,
          marginBottom: isGivenchyMobile ? "40px" : 0,
        }}
      >
        <p
          style={{
            margin: 0,
          }}
        >
          {descriptionStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("description"))
            : t("description")}
        </p>
      </div>
      <div
        tabIndex={-1}
        className="belly"
        style={{
          marginTop: isTDF ? "5px" : isMobile ? "20px" : "40px",
          gap: isTDF ? "0px" : isMobile ? "10px" : "30px",
        }}
      >
        <div
          className="body"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: handleImagesGap(),
          }}
        >
          <BellyChoice
            image={getSleeveAsset("sleeve_1") ?? null}
            onClick={() => {
              value.current = 1;
              setSelected(1);

              requestAnimationFrame(() => {
                continueButtonRef.current?.focus();
              });
            }}
            text={t("size.one")}
            isSelected={selected === 1}
            type="sleeve"
          />
          <BellyChoice
            image={getSleeveAsset("sleeve_2") ?? null}
            onClick={() => {
              value.current = 2;
              setSelected(2);

              requestAnimationFrame(() => {
                continueButtonRef.current?.focus();
              });
            }}
            text={t("size.two")}
            isSelected={selected === 2}
            type="sleeve"
          />
        </div>
        <div className="controls">
          {!isSRP && !isVB && !isGivenchy && !isTDF && (
            <Stepper
              stepsNum={isHast ? 4 : gender === GENDERS.M ? 3 : 2}
              step={4}
              key={1}
            />
          )}
          <button
            tabIndex={0}
            ref={continueButtonRef}
            type="button"
            disabled={!selected}
            className={`continue-button step-belly-button`}
            onMouseEnter={(e) => {
              if (isMobile) return;
              setHovered(true);
              Object.assign(e.currentTarget.style, hoverStylesContinue);
            }}
            onMouseLeave={(e) => {
              setHovered(false);
              Object.assign(e.currentTarget.style, continueButtonStyles);
            }}
            onClick={() => nextStep()}
            style={CTAStyles}
          >
            <div
              style={{ display: "inline-flex", alignItems: "center", gap: 0 }}
            >
              {isTheodore
                ? t("continue")
                : continueButtonStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("continue"))
                  : t("continue")}

              {isIntersport && (
                <span style={arrowContainerStyle} aria-hidden>
                  <ChevronRight style={iconStyle} />
                </span>
              )}
            </div>
          </button>
          <ScanCTA
            nextStep={async () => {
              if (userContext) {
                const resultNewMeasure =
                  await userContext.createNewMeasure("scan");
                if (resultNewMeasure) {
                  setStep(MODAL_STEPS.QR_CODE);
                  setPreviousStep(MODAL_STEPS.CUISSES);
                }
              }
            }}
          />
        </div>
        <MeasuredBy step={step} />
      </div>
    </>
  );
};

export default StepSleeves;
